/* Ported from the vhc prototype's kit (src/pages/AIGenerated/vhc/components) — it had
   no equivalent here, so it moved rather than being replaced. */
import clsx from 'clsx';
import type { CSSProperties } from 'react';

import s from './Skeleton.module.scss';

/**
 * The shape the bone stands in for. `list`, `stat` and `chart` are composites — a row of
 * avatar + two lines, a label over a value, a row of bars — because those three layouts
 * are what the app actually waits on, and rebuilding them out of `block` at every call
 * site is how a skeleton drifts away from the thing it is standing in for.
 */
type SkeletonVariant = 'text' | 'block' | 'circle' | 'list' | 'card' | 'stat' | 'chart';

export interface SkeletonProps {
  variant?: SkeletonVariant;
  width?: number | string;
  height?: number | string;
  /** Number of rows for the 'list' variant (default 3). */
  count?: number;
  className?: string;
}

const toDim = (value?: number | string): string | undefined =>
  typeof value === 'number' ? `${value}px` : value;

const sizeStyle = (
  width?: number | string,
  height?: number | string,
): CSSProperties | undefined => {
  const w = toDim(width);
  const h = toDim(height);
  if (w === undefined && h === undefined) return undefined;
  return { width: w, height: h };
};

const CHART_BAR_HEIGHTS = ['45%', '70%', '55%', '85%', '60%', '75%'];

/**
 * The grey shape a component leaves while its data is in flight. Use it where the layout
 * is known before the data is — a list, a card, a stat — so the page keeps its shape
 * instead of collapsing and jumping back. Where the wait has no shape to preserve, or is
 * short, `LoaderSpinner` is the simpler answer.
 *
 * Every bone is `aria-hidden`: a screen reader gets nothing useful from a placeholder,
 * and the region it sits in should carry `aria-busy` instead.
 *
 * @alias loading placeholder, shimmer, ghost, bone, spinner alternative
 */
export const Skeleton = ({
  variant = 'block',
  width,
  height,
  count = 3,
  className,
}: SkeletonProps) => {
  const style = sizeStyle(width, height);

  if (variant === 'list') {
    return (
      <div className={clsx(s.list, className)} style={style} aria-hidden="true">
        {Array.from({ length: count }, (_, i) => (
          <div key={i} className={s.listRow}>
            <span className={clsx(s.bone, s.circle, s.listAvatar)} />
            <span className={s.listText}>
              <span className={clsx(s.bone, s.text)} style={{ width: '60%' }} />
              <span className={clsx(s.bone, s.text)} style={{ width: '40%' }} />
            </span>
          </div>
        ))}
      </div>
    );
  }

  if (variant === 'stat') {
    return (
      <div className={clsx(s.stat, className)} style={style} aria-hidden="true">
        <span className={clsx(s.bone, s.statLabel)} />
        <span className={clsx(s.bone, s.statValue)} />
      </div>
    );
  }

  if (variant === 'chart') {
    return (
      <div className={clsx(s.chart, className)} style={style} aria-hidden="true">
        {CHART_BAR_HEIGHTS.map((barHeight, i) => (
          <span key={i} className={clsx(s.bone, s.chartBar)} style={{ height: barHeight }} />
        ))}
      </div>
    );
  }

  const variantClass = {
    text: s.text,
    block: s.block,
    circle: s.circle,
    card: s.cardShape,
  }[variant];

  return (
    <span className={clsx(s.bone, variantClass, className)} style={style} aria-hidden="true" />
  );
};
